import React, { useEffect, useMemo, useRef, useState } from "react";
import { HiOutlinePlus, HiOutlineChevronRight, HiOutlineDocumentSearch } from "react-icons/hi";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useHotkeys } from "react-hotkeys-hook";

import API from "../../api";
import { capture } from "../../instrument";
import { readListState, writeListState, PRESCRIPTION_FLAG_LABELS, PRESCRIPTION_WARNING_LABELS, PRESCRIPTION_DOCTOR_STATUS_LABELS } from "../../utils";
import PageLoadingSpinner from "../../components/PageLoadingSpinner";
import SearchFilters from "../../components/SearchFilters";
import SaveViewModal from "../../components/SaveViewModal";
import ViewTabs from "../../components/ViewTabs";
import UploadPrescriptionModal from "../../components/ordonnance/UploadPrescriptionModal";
import useListShortcuts from "../../hooks/useListShortcuts";
import useSavedViews from "../../hooks/useSavedViews";
import useSearchFilters, { SEARCH_OPERATOR } from "../../hooks/useSearchFilters";
import { SHORTCUTS } from "../../utils/shortcuts";
import { notifyShortcut } from "../../utils/shortcutTip";

const STORAGE_KEY = "prescription-list-state";
const PAGE_SIZE = 200;

const CODE_OPERATOR = { ADDED: "ajouté", DELETED: "supprimé", HANDWRITTEN: "manuscrit", MISSING: "absent", REFUSED: "refusé" };

const CODE_OPERATOR_PREFIX = {
  [SEARCH_OPERATOR.IS]: "@codes:",
  [CODE_OPERATOR.ADDED]: "@codes>added:",
  [CODE_OPERATOR.DELETED]: "@codes>deleted:",
  [CODE_OPERATOR.HANDWRITTEN]: "@codes>handwritten:",
  [CODE_OPERATOR.MISSING]: "@codes>not:",
  [CODE_OPERATOR.REFUSED]: "@codes>notInserted:",
};

const REGEX_FIELD_PREFIX = { rpps: "@RPPS:", adeli: "@ADELI:", type: "@type:", doctorStatus: "@doctorStatus:" };
const TYPE_LABELS = { SIL: "SIL", HPRIM: "HPRIM", API: "API" };
const TYPE_QUERY_VALUE = { SIL: "(scan|kalisil)" };
const ENUM_FIELD_PREFIX = { warning: "@warning:", flags: "@flags:", correction: "@correction:", acts: "@acts:", ALD: "@ALD:", indexName: "@indexName:" };
const DATE_OPERATOR = { FROM: "à partir du", UNTIL: "jusqu'au" };
const CORRECTION_LABELS = { added: "Ajout", deleted: "Suppression", refused: "Refus SIL", none: "Aucune" };
const YES_NO_LABELS = { true: "Oui", false: "Non" };

const WARNING_FILTER_LABELS = { "*": "Toutes", ...PRESCRIPTION_WARNING_LABELS };

const FILTER_FIELDS = [
  { key: "date", label: "Date", type: "date", operators: [SEARCH_OPERATOR.IS, DATE_OPERATOR.FROM, DATE_OPERATOR.UNTIL, SEARCH_OPERATOR.WITHIN_LAST_DAYS] },
  { key: "codes", label: "Code", operators: [SEARCH_OPERATOR.IS, ...Object.values(CODE_OPERATOR)], values: [] },
  { key: "correction", label: "Correction", operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT], values: Object.keys(CORRECTION_LABELS), valueLabels: CORRECTION_LABELS },
  { key: "warning", label: "Alerte", operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT], values: Object.keys(WARNING_FILTER_LABELS), valueLabels: WARNING_FILTER_LABELS },
  {
    key: "flags",
    label: "Signalement",
    operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
    values: Object.keys(PRESCRIPTION_FLAG_LABELS),
    valueLabels: PRESCRIPTION_FLAG_LABELS,
  },
  {
    key: "doctorStatus",
    label: "Code médecin",
    operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT],
    values: Object.keys(PRESCRIPTION_DOCTOR_STATUS_LABELS),
    valueLabels: PRESCRIPTION_DOCTOR_STATUS_LABELS,
  },
  { key: "acts", label: "Examens détectés", operators: [SEARCH_OPERATOR.IS], values: ["some", "none"], valueLabels: { some: "Oui", none: "Non" } },
  { key: "ALD", label: "ALD", operators: [SEARCH_OPERATOR.IS], values: Object.keys(YES_NO_LABELS), valueLabels: YES_NO_LABELS },
  { key: "type", label: "Type", operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT], values: Object.keys(TYPE_LABELS), valueLabels: TYPE_LABELS },
  { key: "rpps", label: "RPPS", operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT], values: [] },
  { key: "adeli", label: "ADELI", operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT], values: [] },
  { key: "text", label: "Texte", operators: [SEARCH_OPERATOR.CONTAINS], values: [] },
];

const regexAlternation = (values) => (values.length > 1 ? `(${values.join("|")})` : values[0]);

const toConditionAlternatives = (filter) => {
  const values = filter.values.map((value) => value.trim()).filter(Boolean);
  if (values.length === 0) return [];
  const isNegated = filter.operator === SEARCH_OPERATOR.IS_NOT;
  const negate = (condition) => (isNegated ? `NOT ${condition}` : condition);
  if (filter.field === "codes") return [[`${CODE_OPERATOR_PREFIX[filter.operator]}${regexAlternation(values)}`]];
  if (REGEX_FIELD_PREFIX[filter.field]) {
    const queryValues = filter.field === "type" ? values.map((value) => TYPE_QUERY_VALUE[value] || value) : values;
    return [[negate(`${REGEX_FIELD_PREFIX[filter.field]}${regexAlternation(queryValues)}`)]];
  }
  if (filter.field === "text") return values.map((value) => [`@text:${value}`]);
  if (!ENUM_FIELD_PREFIX[filter.field]) return [];
  const conditions = values.map((value) => negate(`${ENUM_FIELD_PREFIX[filter.field]}${value}`));
  return isNegated ? [conditions] : conditions.map((condition) => [condition]);
};

const buildExplorerQuery = (search, filters) => {
  const alternativesPerFilter = filters
    .filter((filter) => filter.field !== "date")
    .map(toConditionAlternatives)
    .filter((alternatives) => alternatives.length > 0);
  const text = search.trim();
  if (alternativesPerFilter.length === 0) return text;
  if (text) alternativesPerFilter.push([[text]]);
  const groups = alternativesPerFilter.reduce((acc, alternatives) => acc.flatMap((group) => alternatives.map((conditions) => [...group, ...conditions])), [[]]);
  return groups.map((group) => group.join(" AND ")).join(" OR ");
};

const localIsoDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const laterDay = (current, candidate) => (!current || candidate > current ? candidate : current);
const earlierDay = (current, candidate) => (!current || candidate < current ? candidate : current);

const toDateRange = (filters) => {
  let startDate = "";
  let endDate = "";
  for (const filter of filters) {
    const value = filter.values[0];
    if (filter.field !== "date" || !value) continue;
    if (filter.operator === SEARCH_OPERATOR.IS) {
      startDate = laterDay(startDate, value);
      endDate = earlierDay(endDate, value);
    } else if (filter.operator === DATE_OPERATOR.FROM) {
      startDate = laterDay(startDate, value);
    } else if (filter.operator === DATE_OPERATOR.UNTIL) {
      endDate = earlierDay(endDate, value);
    } else if (filter.operator === SEARCH_OPERATOR.WITHIN_LAST_DAYS) {
      const since = new Date();
      since.setDate(since.getDate() - Number(value));
      startDate = laterDay(startDate, localIsoDate(since));
    }
  }
  return { startDate, endDate };
};

const Prescriptions = ({ user }) => {
  const history = useHistory();
  const isSuperAdmin = user.role === "superadmin";
  const location = useLocation();
  const queryClient = useQueryClient();
  const today = localIsoDate(new Date());
  const defaultViews = useMemo(
    () => [
      { _id: "TODAY", name: "Aujourd'hui", filters: [{ id: "default-today", field: "date", operator: SEARCH_OPERATOR.IS, values: [today] }] },
      {
        _id: "TO_CHECK",
        name: "À vérifier",
        filters: [
          { id: "default-check-date", field: "date", operator: SEARCH_OPERATOR.IS, values: [today] },
          { id: "default-check-warning", field: "warning", operator: SEARCH_OPERATOR.IS, values: ["*"] },
        ],
      },
      { _id: "FLAGGED", name: "Signalées", filters: [{ id: "default-flagged", field: "flags", operator: SEARCH_OPERATOR.IS, values: ["MISSING_EXAM", "TRADUCTION_ERROR"] }] },
      { _id: "ALL", name: "Toutes", filters: [] },
    ],
    [today],
  );

  const savedState = useMemo(() => readListState(STORAGE_KEY), []);
  const urlQuery = useMemo(() => new URLSearchParams(location.search).get("q") || "", [location.search]);
  const [search, setSearch] = useState(urlQuery || savedState?.search || "");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const listRef = useRef(null);
  const selectedIndexRef = useRef(savedState?.selectedIndex || 0);
  const [selectedIndex, setSelectedIndex] = useState(savedState?.selectedIndex || 0);

  const [filterFields, setFilterFields] = useState(FILTER_FIELDS);
  const { filters: activeFilters, setFilters: setActiveFilters } = useSearchFilters(filterFields, {
    initialFilters: urlQuery ? [] : savedState?.filters || defaultViews[0].filters,
  });

  useEffect(() => {
    if (!urlQuery) return;
    setSearch(urlQuery);
    setActiveFilters([]);
  }, [urlQuery, setActiveFilters]);

  useEffect(() => {
    const timerId = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timerId);
  }, [search]);

  const { startDate, endDate } = useMemo(() => toDateRange(activeFilters), [activeFilters]);
  const q = useMemo(() => buildExplorerQuery(debouncedSearch, activeFilters), [debouncedSearch, activeFilters]);

  useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [q, startDate, endDate]);

  const {
    data: prescriptions = [],
    isLoading: isLoadingPrescriptions,
    isFetching: isFetchingPrescriptions,
    isError: isErrorPrescriptions,
  } = useQuery({
    queryKey: ["prescriptions", { q, startDate, endDate, limit }],
    queryFn: async () => {
      const response = await API.get(`/prescription/explorer?limit=${limit}&startDate=${startDate}&endDate=${endDate}&q=${encodeURIComponent(q)}`);
      if (!response.ok) {
        const error = new Error(response.message);
        capture(error);
        throw error;
      }
      return response.data;
    },
    placeholderData: (previousData) => previousData,
  });

  const visiblePrescriptions = useMemo(() => prescriptions.slice(0, limit), [prescriptions, limit]);
  const hasMore = prescriptions.length > limit;

  useEffect(() => {
    if (!isSuperAdmin) return;
    const knownIndexNames = filterFields.find((field) => field.key === "indexName")?.values || [];
    const indexNames = [...new Set([...knownIndexNames, ...prescriptions.map((prescription) => prescription.indexName).filter(Boolean)])].sort();
    if (indexNames.length === knownIndexNames.length) return;
    setFilterFields([...FILTER_FIELDS, { key: "indexName", label: "Établissement", operators: [SEARCH_OPERATOR.IS, SEARCH_OPERATOR.IS_NOT], values: indexNames }]);
  }, [isSuperAdmin, prescriptions, filterFields]);

  const virtualizer = useVirtualizer({
    count: visiblePrescriptions.length,
    getScrollElement: () => listRef.current,
    initialOffset: savedState?.scrollTop || 0,
    estimateSize: () => 53,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();

  const resetSelection = () => {
    selectedIndexRef.current = 0;
    setSelectedIndex(0);
    virtualizer.scrollToIndex(0);
    writeListState(STORAGE_KEY, { scrollTop: 0, selectedIndex: 0 });
  };

  const onFiltersChange = (nextFilters) => {
    setActiveFilters(nextFilters);
    resetSelection();
  };

  const { savedViews, activeViewId, saveModalOpen, setSaveModalOpen, handleSelectView, isViewActive, handleConfirmSaveView, handleDeleteView, saveOptions } = useSavedViews({
    collectionName: "prescription",
    defaultViews,
    activeFilters,
    setActiveFilters: onFiltersChange,
    initialViewId: urlQuery ? "ALL" : savedState?.viewId,
  });

  useEffect(() => {
    writeListState(STORAGE_KEY, { search, filters: activeFilters, viewId: activeViewId });
  }, [search, activeFilters, activeViewId]);

  const navigateToPrescription = (item) => {
    const ids = visiblePrescriptions.map((p) => p._id);
    writeListState(STORAGE_KEY, { scrollTop: listRef.current.scrollTop, selectedIndex: ids.indexOf(item._id) });
    history.push({ pathname: `/prescription/${item._id}`, state: { filteredIds: ids } });
  };

  const onNavigateDown = () => {
    const next = selectedIndexRef.current >= visiblePrescriptions.length - 1 ? 0 : selectedIndexRef.current + 1;
    selectedIndexRef.current = next;
    setSelectedIndex(next);
    virtualizer.scrollToIndex(next);
  };

  const onNavigateUp = () => {
    const prev = selectedIndexRef.current <= 0 ? visiblePrescriptions.length - 1 : selectedIndexRef.current - 1;
    selectedIndexRef.current = prev;
    setSelectedIndex(prev);
    virtualizer.scrollToIndex(prev);
  };

  const onRowMouseMove = (event, index) => {
    const pointerDidNotMove = event.movementX === 0 && event.movementY === 0;
    if (pointerDidNotMove || selectedIndexRef.current === index) return;
    selectedIndexRef.current = index;
    setSelectedIndex(index);
  };

  useListShortcuts(
    {
      onUp: onNavigateUp,
      onDown: onNavigateDown,
      onOpen: ({ newTab } = {}) => {
        const prescription = visiblePrescriptions[selectedIndexRef.current];
        if (!prescription) return;
        if (newTab) window.open(`/prescription/${prescription._id}`, "_blank");
        else navigateToPrescription(prescription);
      },
      enabled: !isUploadOpen && !saveModalOpen,
    },
    [visiblePrescriptions, virtualizer, isUploadOpen, saveModalOpen],
  );

  useHotkeys(SHORTCUTS.CREATE.keys, () => setIsUploadOpen(true), { preventDefault: true });

  const onSearchChange = (e) => {
    setSearch(e.target.value);
    resetSelection();
  };

  const onUploaded = (createdIds) => {
    setIsUploadOpen(false);
    queryClient.invalidateQueries({ queryKey: ["prescriptions"] });
    if (createdIds.length === 1) history.push(`/prescription/${createdIds[0]}`);
  };

  if (isLoadingPrescriptions) {
    return <PageLoadingSpinner />;
  }

  if (isErrorPrescriptions) {
    return (
      <div className="flex min-h-[calc(100vh-60px)] flex-col items-center justify-center gap-3 bg-gray-50">
        <span className="text-sm text-red-500">Erreur lors du chargement des ordonnances</span>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-60px)] bg-gray-50 p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <h1 className="text-xl font-semibold text-gray-900">Ordonnances</h1>
          <ViewTabs defaultViews={defaultViews} savedViews={savedViews} isViewActive={isViewActive} onSelectView={handleSelectView} onDeleteView={handleDeleteView} />
        </div>
        <button
          type="button"
          className="flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          onClick={() => {
            setIsUploadOpen(true);
            notifyShortcut("CREATE");
          }}
        >
          <HiOutlinePlus className="h-4 w-4" />
          <span>Nouvelle ordonnance</span>
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <SearchFilters
          fields={filterFields}
          filters={activeFilters}
          onFiltersChange={onFiltersChange}
          search={search}
          onSearchChange={onSearchChange}
          placeholder="Rechercher un numéro de dossier..."
          saveOptions={saveOptions}
          isLoading={isFetchingPrescriptions}
        />

        <div ref={listRef} className="max-h-[calc(100vh-252px)] overflow-y-auto">
          {visiblePrescriptions.length > 0 && (
            <div style={{ height: totalHeight, position: "relative" }}>
              {virtualItems.map((virtualRow) => {
                const prescription = visiblePrescriptions[virtualRow.index];
                const createdAt = new Date(prescription.createdAt);
                return (
                  <div
                    key={prescription._id}
                    style={{ position: "absolute", top: 0, left: 0, right: 0, height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
                  >
                    <div
                      style={{ height: virtualRow.size }}
                      data-active={virtualRow.index === selectedIndex || undefined}
                      onMouseMove={(event) => onRowMouseMove(event, virtualRow.index)}
                      className="group flex cursor-pointer items-center border-b border-gray-100 px-4 transition-colors hover:bg-gray-50 data-[active=true]:bg-primary-50/50"
                      onClick={() => navigateToPrescription(prescription)}
                    >
                      <span className="mr-4 w-[88px] shrink-0 text-xs tabular-nums text-gray-400">
                        {createdAt.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })} ·{" "}
                        {createdAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                      <span className="min-w-0 truncate text-sm font-medium text-gray-900">
                        {prescription.silAdmissionNumber || <span className="italic text-gray-400">Sans numéro de dossier</span>}
                      </span>
                      {isSuperAdmin && <span className="ml-3 shrink-0 font-mono text-[11px] uppercase tracking-wide text-gray-400">{prescription.indexName}</span>}
                      <div className="ml-auto flex flex-shrink-0 items-center gap-4 pl-4">
                        <span className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
                          {prescription.type}
                        </span>
                        <HiOutlineChevronRight className="h-4 w-4 text-gray-300" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {hasMore && (
            <div className="flex justify-center py-3">
              <button
                type="button"
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
                onClick={() => setLimit(limit + PAGE_SIZE)}
              >
                {"Charger plus d'ordonnances"}
              </button>
            </div>
          )}

          {visiblePrescriptions.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <HiOutlineDocumentSearch className="mb-3 h-8 w-8 text-gray-300" />
              <p className="text-sm">Aucune ordonnance</p>
              <p className="mt-1 text-xs">Essayez de modifier la date, la recherche ou les filtres.</p>
            </div>
          )}
        </div>
      </div>

      {isUploadOpen && <UploadPrescriptionModal onClose={() => setIsUploadOpen(false)} onUploaded={onUploaded} />}
      {saveModalOpen && <SaveViewModal onClose={() => setSaveModalOpen(false)} onConfirm={handleConfirmSaveView} />}
    </div>
  );
};

export default Prescriptions;
